import React from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight, Quote } from 'lucide-react';
import Reveal from '../components/Reveal';

const Para = ({ children }) => (
  <p className="text-fire-cream/75 font-body leading-[1.85] mb-5">{children}</p>
);

const PageHeader = ({ kicker, title, sub }) => (
  <div className="pt-32 pb-14 text-center ember-texture">
    <div className="max-w-3xl mx-auto px-6">
      <Flame className="w-8 h-8 text-fire-gold mx-auto mb-5 animate-flicker" strokeWidth={1.4} />
      <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">{kicker}</span>
      <h1 className="font-display text-4xl md:text-6xl text-fire-cream mt-4">{title}</h1>
      {sub && <p className="font-cormorant italic text-fire-goldlight text-2xl mt-4">{sub}</p>}
    </div>
  </div>
);

const MyStory = () => {
  return (
    <div className="bg-fire-ink">
      <Seo title="My Story" path="/my-story" description="Dr. Mischell 'Dr. Gee' Glenn shares her journey through abandonment, heartbreak, single motherhood and rebuilding — the story behind the After the Fire™ movement." />
      <PageHeader kicker="MY STORY" title="Meet Mischell" sub="Before you call me Dr. Gee…" />

      <section className="pb-24">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-[minmax(0,320px)_1fr] gap-10 items-start">
            <Reveal className="md:sticky md:top-28">
              <div className="rounded-2xl overflow-hidden hover-lift">
                <img src="/brand/mystory-restored.jpg" alt="Dr. Mischell Gee" className="w-full h-auto" />
              </div>
              <div className="card-dark rounded-xl p-5 mt-5 text-center">
                <p className="font-script text-3xl text-fire-goldlight">Dr. Gee</p>
                <p className="font-body text-xs text-fire-cream/60 mt-1">Pastor • Author • Recording Artist • Speaker</p>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <Para>
                If you only meet me on a stage, you might get the wrong idea about me. You’ll see the preacher, the speaker, the recording artist, the author, the businesswoman, the mother, the grandmother — the woman with the microphone, the big personality, the smile and the I SURVIVED shirt.
              </Para>
              <Para>
                But you won’t immediately see the fires. And believe me — there have been fires. My story didn’t begin with a microphone in my hand. It began with a little girl learning how to survive things she didn’t have the words to explain.
              </Para>
              <Para>
                I know abandonment. I know instability. I know what it feels like to lose people you thought would always be there. And because I learned how to survive early, I became very good at being strong. Maybe too good. I could preach while hurting. I could sing while hurting. I could raise my children while hurting. I knew how to function in the fire. What I didn’t know was how to live once the fire was over.
              </Para>

              <h2 className="font-display text-2xl md:text-3xl text-fire-cream mt-10 mb-5">The House Was Me</h2>
              <Para>
                There came a point when I had to look beyond everything that had happened around me and confront what had happened inside me. Because sometimes the strongest woman in the room is also the woman who has become the best at hiding the smoke.
              </Para>
              <Para>
                I had spent so much time being somebody’s mother, somebody’s pastor, somebody’s partner, somebody’s problem-solver — that I had to ask a frightening question: <span className="text-fire-goldlight italic">“Who is Mischell when she isn’t being everything everybody else needs?”</span> That question changed my life.
              </Para>

              <div className="card-dark rounded-2xl p-8 my-8 border-l-2 border-fire-gold">
                <Quote className="w-7 h-7 text-fire-gold/60 mb-3" />
                <p className="font-cormorant text-2xl text-fire-cream italic leading-relaxed">
                  The house was me. My peace was worth saving. My confidence was worth saving. My identity was worth saving. Mischell was worth saving.
                </p>
              </div>

              <h2 className="font-display text-2xl md:text-3xl text-fire-cream mt-10 mb-5">And Then I Came Out of the Fire</h2>
              <Para>
                Not perfect. Not without scars. Not with every question answered. But alive. And somewhere during the rebuilding, I realized something: I wasn’t supposed to hide my scars. I was supposed to use them as evidence. Evidence that you can survive. Evidence that you can start over. Evidence that heartbreak doesn’t get the final word.
              </Para>
              <Para>
                That’s why I tell women: <span className="text-fire-goldlight">Just because you got burned doesn’t mean you have to spend the rest of your life smelling like smoke.</span>
              </Para>

              <h2 className="font-display text-2xl md:text-3xl text-fire-cream mt-10 mb-5">Why I Want to Meet You</h2>
              <Para>
                After everything I’ve lived through, I’m not interested in building a platform where women only watch me from a distance. I want community. I want rooms filled with women who have stories — women who survived things they don’t always talk about, women who are successful but tired, women who love God but still have questions, women who are rebuilding.
              </Para>
              <Para>
                You don’t have to impress me. Bring your story. Bring your questions. Bring your scars. Bring your laughter. Because I’m not standing on the other side of the fire yelling, “Look at me.” I’m standing here yelling: <span className="text-fire-goldlight italic">“There’s an exit! Come this way!”</span>
              </Para>
              <Para>
                I’m Dr. Mischell “Dr. Gee” Glenn. But before the titles, I’m Mischell. A woman who has loved, lost, raised children, started over, made mistakes, survived heartbreak, and found herself again — and discovered that surviving the fire wasn’t the end of her story. It was the beginning of her assignment.
              </Para>
              <Para>
                <span className="font-display text-2xl text-gradient-gold">I SURVIVED.</span> And if you’re still standing… Sis, so did you. Now let’s figure out what we’re going to do with the life that survived. Welcome to After the Fire™.
              </Para>

              <div className="flex flex-wrap gap-4 mt-10">
                <Link to="/fireside" className="btn-gold rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 text-sm">
                  Schedule a Fireside Call <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/experience" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold text-sm">
                  Come to an Experience
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyStory;
